import Phaser from 'phaser';
import { TextureKey, type PipeSide } from '../assets/manifest.js';
import { TUNING } from '../config/tuning.js';
import { byAging } from '../core/life.js';
import type { WorldScale } from '../core/WorldScale.js';

const T = TUNING.pipes;

/** The stretch between two gates (pixels, coordinates at the time it is announced). */
export interface CourseSection {
  /** left / right end */
  x0: number;
  x1: number;
  /** gap centre of the gate (or start height) on the left / on the right */
  y0: number;
  y1: number;
}

interface GateRoll {
  centre: number;
  gap: number;
}

/** One obstacle: a pipe from the top and one from the bottom with a gap in between. */
class PipePair {
  readonly top: Phaser.GameObjects.Image;
  readonly bottom: Phaser.GameObjects.Image;
  /** left edge in pixels */
  x = 0;
  gapTop = 0;
  gapBottom = 0;
  scored = false;
  active = false;

  constructor(scene: Phaser.Scene, width: number, height: number, mirrored: boolean) {
    this.bottom = scene.add.image(0, 0, TextureKey.Pipe).setOrigin(0, 0).setDisplaySize(width, height).setFlipX(mirrored);
    this.top = scene.add.image(0, 0, TextureKey.PipeTop).setOrigin(0, 1).setDisplaySize(width, height).setFlipX(mirrored);
    this.setVisible(false);
  }

  place(x: number, gapTop: number, gapBottom: number): void {
    this.x = x;
    this.gapTop = gapTop;
    this.gapBottom = gapBottom;
    this.scored = false;
    this.active = true;
    this.top.setPosition(x, gapTop);
    this.bottom.setPosition(x, gapBottom);
    this.setVisible(true);
  }

  moveTo(x: number): void {
    this.x = x;
    this.top.x = x;
    this.bottom.x = x;
  }

  release(): void {
    this.active = false;
    this.setVisible(false);
  }

  setDepth(d: number): void {
    this.top.setDepth(d);
    this.bottom.setDepth(d);
  }

  private setVisible(v: boolean): void {
    this.top.setVisible(v);
    this.bottom.setVisible(v);
  }
}

/**
 * Spawns, scrolls, recycles and collision-tests the pipe pairs.
 * Gap positions come from a seeded RNG, so a run can be reproduced from its seed.
 * The next gate is always rolled one step ahead, so the stretch up to it can be
 * announced (`onSection` → coins) while it is still off-screen.
 */
export class PipeField {
  private readonly pool: PipePair[] = [];
  private readonly rng = new Phaser.Math.RandomDataGenerator();
  private readonly pipeW: number;
  /** the gate that is spawned next */
  private next: GateRoll = { centre: 0, gap: 0 };
  /** pixel distance scrolled since the last spawn */
  private sinceSpawn = 0;
  /** 0 = young … 1 = old (see core/life.ts): older players get wider gates with smaller jumps */
  private aging = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly ws: WorldScale,
    /** width of the playfield (pixels) – pipes spawn at its right edge */
    private readonly viewWidth: number,
    /** y (pixels) of the ground surface */
    private readonly floorY: number,
    private readonly depth: number,
    /** called for every stretch between two gates, before it scrolls into view */
    private readonly onSection: (s: CourseSection) => void,
    /** dark side face of the columns: as in the artwork (left) or mirrored (right) */
    private readonly side: PipeSide = 'left',
  ) {
    this.pipeW = ws.px(T.width);
  }

  /** Remove all pipes and prepare a new sequence. `startY` = where the player starts (pixels). */
  reset(seed: string, startY: number): void {
    for (const p of this.pool) p.release();
    this.rng.sow([seed]);
    this.aging = 0;
    this.next = this.roll(startY);
    // first pipe appears `firstPipeOffset` after the right edge is reached
    this.sinceSpawn = this.ws.px(T.spacing - T.firstPipeOffset);
    const firstX = this.viewWidth + this.ws.px(T.firstPipeOffset);
    this.onSection({ x0: this.viewWidth, x1: firstX, y0: startY, y1: this.next.centre });
  }

  /** Aging (0…1) of the player when they will reach the gates spawned from now on. */
  setAging(aging: number): void {
    this.aging = aging;
  }

  /** Scroll by `dx` pixels, spawn/recycle pipes. */
  scroll(dx: number): void {
    for (const p of this.pool) {
      if (!p.active) continue;
      p.moveTo(p.x - dx);
      if (p.x + this.pipeW < 0) p.release();
    }
    this.sinceSpawn += dx;
    const spacing = this.ws.px(T.spacing);
    while (this.sinceSpawn >= spacing) {
      this.sinceSpawn -= spacing;
      this.spawn(this.viewWidth - this.sinceSpawn);
    }
  }

  /** Number of pipes whose right edge passed `x` since the last call. */
  collectPassed(x: number): number {
    let n = 0;
    for (const p of this.pool) {
      if (p.active && !p.scored && p.x + this.pipeW < x) {
        p.scored = true;
        n++;
      }
    }
    return n;
  }

  /** Gap centre (pixels) of the next gate the figure at `x` still has to pass (autopilot of the demo). */
  gapAhead(x: number, r: number): { centre: number; distance: number } {
    let next: PipePair | null = null;
    for (const p of this.pool) if (p.active && p.x + this.pipeW > x - r && (!next || p.x < next.x)) next = p;
    if (!next) return { centre: this.next.centre, distance: Infinity };
    return { centre: (next.gapTop + next.gapBottom) / 2, distance: next.x - x };
  }

  /** width of a pipe in pixels */
  get width(): number {
    return this.pipeW;
  }

  /** Circle (bird) vs. pipe rectangles. */
  hits(cx: number, cy: number, r: number): boolean {
    for (const p of this.pool) {
      if (!p.active || cx + r < p.x || cx - r > p.x + this.pipeW) continue;
      // inside the pipe column: hit unless the circle is fully inside the gap
      const nearestX = Phaser.Math.Clamp(cx, p.x, p.x + this.pipeW);
      const dx = cx - nearestX;
      if (cy - r < p.gapTop) {
        const dy = cy - Math.min(cy, p.gapTop);
        if (dy === 0 || dx * dx + dy * dy < r * r) return true;
      }
      if (cy + r > p.gapBottom) {
        const dy = cy - Math.max(cy, p.gapBottom);
        if (dy === 0 || dx * dx + dy * dy < r * r) return true;
      }
    }
    return false;
  }

  private spawn(x: number): void {
    const { centre, gap } = this.next;
    this.obtain().place(x, centre - gap / 2, centre + gap / 2);
    this.next = this.roll(centre);
    this.onSection({ x0: x + this.pipeW, x1: x + this.ws.px(T.spacing), y0: centre, y1: this.next.centre });
  }

  /** Next gate, reachable from a gate (or start height) at `fromCentre`. */
  private roll(fromCentre: number): GateRoll {
    const gap = this.ws.px(byAging(T.gap, T.gapOld, this.aging));
    const margin = this.ws.px(T.margin);
    const minC = margin + gap / 2;
    const maxC = this.floorY - margin - gap / 2;
    const shift = this.ws.px(byAging(T.maxGapShift, T.maxGapShiftOld, this.aging));
    const lo = Math.max(minC, Math.min(maxC, fromCentre - shift));
    const hi = Math.min(maxC, Math.max(minC, fromCentre + shift));
    return { centre: this.rng.realInRange(lo, hi), gap };
  }

  private obtain(): PipePair {
    let p = this.pool.find((q) => !q.active);
    if (!p) {
      p = new PipePair(this.scene, this.pipeW, this.ws.px(1000), this.side === 'right');
      p.setDepth(this.depth);
      this.pool.push(p);
    }
    return p;
  }
}
